
import { useEffect, useCallback } from 'react';
import axios from 'axios';
import { useMultipleEditionStore } from '../stores/useMultipleEditionStore';

const userEdited = new Set(); // "articleId-attributeId" → el usuario editó manualmente

const MAX_CHAIN_DEPTH = 5; // protección contra dependencias circulares

export const useAutoPrefill = () => {
    const setValue = useMultipleEditionStore((s) => s.setMultipleDataObjValue);
    const setApproval = useMultipleEditionStore((s) => s.setMultipleDataObjAproval);
    const rules = useMultipleEditionStore((s) => s.rules);
    const setRules = useMultipleEditionStore((s) => s.setRules);

    const markAsEdited = useCallback((articleId, attributeId) => {
        userEdited.add(`${articleId}-${attributeId}`);
    }, []);

    const applyRules = useCallback(
        (articleId, sourceAttributeId, newValue, depth = 0) => {
            if (!rules || rules.length === 0 || depth > MAX_CHAIN_DEPTH) return;

            const relevantRules = rules.filter(r => r.source_attribute_id === sourceAttributeId);

            const affectedTargetIds = [...new Set(relevantRules.map(r => r.target_attribute_id))];

            affectedTargetIds.forEach(targetId => {
                const targetKey = `${articleId}-${targetId}`;

                if (userEdited.has(targetKey)) return;

                const matchingRule = relevantRules.find(rule => {
                    if (rule.target_attribute_id !== targetId) return false;
                    if (rule.source_value === null) return true;
                    return Array.isArray(rule.source_value) && rule.source_value.includes(newValue);
                });

                if (matchingRule) {
                    setValue(articleId, targetId, matchingRule.target_value);
                    setApproval(articleId, targetId, 1, null);
                    applyRules(articleId, targetId, matchingRule.target_value, depth + 1);
                }
            });
        },
        [rules, setValue, setApproval]
    );

    const applyInitialRules = useCallback(
        (articleId, articleData) => {
            if (!rules || rules.length === 0 || !articleId || !articleData) return;

            articleData.forEach((attr) => {
                if (attr.value) {
                    applyRules(articleId, attr.id_attribute, attr.value);
                }
            });
        },
        [rules, applyRules]
    );

    const evaluateRules = useCallback((sourceAttributeId, newValue) => {
        if (!rules || rules.length === 0) return [];
        return rules.filter((rule) => {
            if (rule.source_attribute_id !== sourceAttributeId) return false;
            if (rule.source_value === null) return true;
            return (
                Array.isArray(rule.source_value) &&
                rule.source_value.includes(newValue)
            );
        });
    }, [rules]);

    return { applyRules, applyInitialRules, markAsEdited, evaluateRules, rules};
};